import type { PlaceIndexEntry, SearchParagraph } from '../../data/types';

/** Normaliza igual que `search_form` en el ETL —minúsculas, sin diacríticos, la puntuación
 *  convertida en espacio— y, sobre todo, **sin cambiar la longitud de la cadena**: cada carácter del
 *  original tiene su carácter en la forma normalizada, así que una posición encontrada aquí vale
 *  tal cual sobre el texto original. El texto de 1884 escribe «ábside» y «á»; quien busca teclea
 *  «abside» y «a». La eñe se descompone como las demás, así que «Peña» y «pena» se buscan igual;
 *  es lo mismo que hace el ETL y lo que espera quien teclea sin acentos. */
export function normalize(s: string): string {
  let out = '';
  // se recorre por unidad UTF-16, no por punto de código, para que la posición se conserve exacta
  for (let i = 0; i < s.length; i++) {
    const n = s[i].normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    // un carácter del original tiene que seguir ocupando uno: si la descomposición da más de uno
    // (ligaduras, mayúsculas raras), nos quedamos con el primero.
    const c = n[0] ?? ' ';
    out += /[a-z0-9]/.test(c) ? c : ' ';
  }
  return out;
}

/** Patrón de la consulta: cada espacio admite una tirada de espacios, porque la puntuación del
 *  original se convierte en espacios y «Salamanca, Ávila» normaliza con dos. */
function pattern(query: string): RegExp | null {
  const parts = normalize(query).split(' ').filter(Boolean);
  // tras normalizar solo quedan [a-z0-9] y espacios: no hay nada que escapar
  return parts.join('').length < 3 ? null : new RegExp(parts.join(' +'), 'g');
}

export interface TextHit {
  paragraphId: string;
  chapterId: string;
  printedPage?: number | null;
  /** fragmento del texto original alrededor de la primera aparición */
  before: string;
  match: string;
  after: string;
  count: number;
}

/** Busca la frase completa, no palabras sueltas: es lo que espera quien busca «casa de las Conchas».
 *  El índice guarda el texto original; la normalización se hace aquí y conserva las posiciones. */
export function findInParagraphs(rows: SearchParagraph[], query: string, limit = 60): TextHit[] {
  const re = pattern(query);
  if (!re) return [];
  const hits: TextHit[] = [];
  for (const r of rows) {
    const norm = normalize(r.t);
    re.lastIndex = 0;
    const first = re.exec(norm);
    if (!first) continue;
    let count = 1;
    while (re.exec(norm)) count++;
    const a = first.index;
    const b = a + first[0].length;
    hits.push({
      paragraphId: r.i, chapterId: r.c, printedPage: r.p, count,
      before: r.t.slice(Math.max(0, a - 70), a), match: r.t.slice(a, b), after: r.t.slice(b, b + 70),
    });
    if (hits.length >= limit) break;
  }
  return hits;
}

/** Busca un lugar en todo el libro, por su nombre actual o por cualquiera de los que le da Quadrado. */
export function findPlaces(index: PlaceIndexEntry[], query: string, limit = 40): PlaceIndexEntry[] {
  const q = normalize(query);
  if (q.length < 2) return [];
  const score = (p: PlaceIndexEntry) => {
    const names = [p.n, ...(p.q ?? [])].map(normalize);
    if (names.some((n) => n === q)) return 0;
    if (names.some((n) => n.startsWith(q))) return 1;
    if (names.some((n) => n.includes(q))) return 2;
    if (p.m && normalize(p.m).includes(q)) return 3;
    return 99;
  };
  return index.map((p) => [score(p), p] as const).filter(([s]) => s < 99)
    .sort((a, b) => a[0] - b[0] || a[1].n.localeCompare(b[1].n, 'es')).slice(0, limit).map(([, p]) => p);
}
